import { useCallback, useMemo, useState } from "react";
import Select from "react-select";
import { getSelectProps } from "utils/helperFunctions";
import { useAppSelector } from "utils/redux/hooks";
import { selectAllCompanyUsersById } from "app/containers/Global/slice";
import { getUserOption } from "app/components/GlobalNavbar/helpers";
import { SearchOption } from "app/components/GlobalNavbar/types";
import AvatarCircle from "../AvatarCircle";

interface Props {
  selectedUserAccountId?: number | null;
  excludeUserAccountIds?: number[];
  onUserSelected?: (userAccountId: number | null) => void;
  placeholderText?: string;
  isDisabled?: boolean;
  includeEmail?: boolean;
}

const UserSelector = ({
  excludeUserAccountIds,
  onUserSelected,
  placeholderText = "Search by name or email...",
  isDisabled = false,
  includeEmail = false,
}: Readonly<Props>) => {
  const usersById = useAppSelector(selectAllCompanyUsersById);
  const [userLookUpInput, setUserLookUpInput] = useState("");
  const { selectStyles, components } = getSelectProps("search");

  const userOptions = useMemo(() => {
    const options: SearchOption[] = [];

    Object.keys(usersById).forEach((uId) => {
      const userId = Number(uId);
      if (excludeUserAccountIds?.includes(userId)) return;
      const userOption = getUserOption(usersById, userId);
      if (userOption) {
        options.push(userOption);
      }
    });
    return options;
  }, [excludeUserAccountIds, usersById]);

  const getMemberOption = useCallback(
    (searchOption: SearchOption) => {
      const userAccountId = Number(searchOption.value);
      const userInfo = usersById[userAccountId];
      if (!userInfo) return null;
      const avatarCircle = (
        <AvatarCircle
          name={searchOption.label}
          userAccountId={userAccountId}
          size="small"
        />
      );

      return (
        <div className="member-option" style={{ alignItems: "center" }}>
          {avatarCircle}
          <div className="member-info">
            <span className="member-name text-md">{searchOption.label}</span>
            {includeEmail && (
              <span className="text-xs text-muted">
                {userInfo.emailAddress}
              </span>
            )}
          </div>
        </div>
      );
    },
    [includeEmail, usersById]
  );

  return (
    <Select<SearchOption>
      isClearable
      isSearchable
      placeholder={placeholderText}
      components={components}
      inputValue={userLookUpInput}
      styles={selectStyles}
      options={userOptions}
      value={null}
      onInputChange={(e, actionMeta) => {
        if (actionMeta.action === "input-change") {
          setUserLookUpInput(e);
        }
      }}
      onChange={(e) => {
        onUserSelected?.(Number(e?.value) || null);
      }}
      formatOptionLabel={getMemberOption}
      filterOption={(option, inputValue) => {
        if (!inputValue) return true;
        const userAccountId = Number(option.value);
        const user = usersById[userAccountId];
        if (!user) return false;
        const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""} ${
          user.emailAddress ?? ""
        }`;
        return fullName.toLowerCase().includes(inputValue.toLowerCase());
      }}
      className="w-100"
      isDisabled={isDisabled}
    />
  );
};

export default UserSelector;
